import React, { useEffect, useMemo, useState } from "react";
import { confirmDialog } from "../../utils/toast";

// 슈퍼어드민 전용 — 회사별 지점(branch) 관리.
// 회사가 늘어나도 감당되도록: [회사 목록(검색)] → 회사 클릭 시 [그 회사 지점 팝업]에서 지점 추가/삭제.
// 새 회사는 [+ 새 회사 등록]으로 (회사 + 첫 지점)을 함께 등록한다.
// 데이터 모델상 회사는 '지점이 1개 이상 있을 때만' 목록에 존재. 지점 없는 회사는 가입 시 회사명만 입력.
export default function BranchSettings() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null); // 지점 팝업 대상 회사명
  const [showAddCompany, setShowAddCompany] = useState(false);

  // 새 회사 등록 모달 입력
  const [newCompany, setNewCompany] = useState("");
  const [newFirstBranch, setNewFirstBranch] = useState("");
  // 회사 팝업 내 지점 추가 입력
  const [addBranchName, setAddBranchName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetch("/api/admin/branches", { credentials: "include" });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || data?.error || "지점 목록을 불러오지 못했습니다.");
      }
      setBranches(Array.isArray(data.branches) ? data.branches : []);
    } catch (err) {
      setError(err.message || "지점 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // 회사별 집계: [{ company, branches[], branchCount, userTotal }]
  const companies = useMemo(() => {
    const map = new Map();
    branches.forEach((b) => {
      if (!map.has(b.companyName)) {
        map.set(b.companyName, { company: b.companyName, branches: [], userTotal: 0 });
      }
      const c = map.get(b.companyName);
      c.branches.push(b);
      c.userTotal += Number(b.userCount || 0);
    });
    return Array.from(map.values())
      .map((c) => ({ ...c, branchCount: c.branches.length }))
      .sort((a, b) => a.company.localeCompare(b.company, "ko"));
  }, [branches]);

  const filteredCompanies = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) => c.company.toLowerCase().includes(q));
  }, [companies, search]);

  const selectedCompanyData = useMemo(
    () => companies.find((c) => c.company === selectedCompany) || null,
    [companies, selectedCompany]
  );

  const handleAddCompany = async () => {
    const company = String(newCompany || "").trim();
    const branch = String(newFirstBranch || "").trim();
    if (company.length < 2 || branch.length < 2) {
      alert("회사명과 첫 지점명을 각각 2자 이상 입력해주세요.");
      return;
    }
    await postBranch(company, branch, () => {
      setNewCompany("");
      setNewFirstBranch("");
      setShowAddCompany(false);
      setSearch(company);
    });
  };

  const handleAddBranchToCompany = async () => {
    const branch = String(addBranchName || "").trim();
    if (!selectedCompany || branch.length < 2) {
      alert("지점명을 2자 이상 입력해주세요.");
      return;
    }
    await postBranch(selectedCompany, branch, () => setAddBranchName(""));
  };

  const postBranch = async (company, branch, onOk) => {
    try {
      setSaving(true);
      const res = await fetch("/api/admin/branch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ companyName: company, branchName: branch }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        alert(data?.message || data?.error || "지점 등록 실패");
        return;
      }
      await load();
      if (onOk) onOk();
    } catch (err) {
      alert("지점 등록 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBranch = async (b) => {
    const extra =
      b.userCount > 0
        ? `\n\n※ 이 지점 소속 계정 ${b.userCount}명의 소속은 유지되고, 가입 목록에서만 사라집니다.`
        : "";
    if (!(await confirmDialog(`[${b.companyName} · ${b.branchName}] 지점을 삭제하시겠습니까?${extra}`, { title: "지점 삭제", danger: true, confirmText: "삭제" }))) return;
    try {
      const res = await fetch("/api/admin/branch/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ branchIdx: b.branchIdx }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        alert(data?.message || data?.error || "지점 삭제 실패");
        return;
      }
      await load();
    } catch (err) {
      alert("지점 삭제 중 오류가 발생했습니다.");
    }
  };

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-[#1F2A3B]">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="text-base font-bold text-gray-900 dark:text-white">지점 관리 (회사별)</h3>
        <button
          type="button"
          onClick={() => setShowAddCompany(true)}
          className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-500"
        >
          + 새 회사 등록
        </button>
      </div>
      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
        회사를 검색·선택하면 그 회사의 지점만 팝업으로 관리합니다. 지점 없는 회사는 가입 시 회사명만 입력합니다.
      </p>

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="회사명 검색"
        className="mb-3 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
      />

      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-300">불러오는 중...</div>
      ) : filteredCompanies.length === 0 ? (
        <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-300">
          {search ? "검색 결과가 없습니다." : "등록된 회사가 없습니다. [+ 새 회사 등록]으로 추가하세요."}
        </div>
      ) : (
        <div className="max-h-[320px] space-y-1.5 overflow-y-auto pr-1">
          {filteredCompanies.map((c) => (
            <button
              key={c.company}
              type="button"
              onClick={() => setSelectedCompany(c.company)}
              className="flex w-full items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-2.5 text-left transition hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-white/5"
            >
              <span className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-white">
                {c.company}
              </span>
              <span className="flex shrink-0 items-center gap-1.5">
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
                  지점 {c.branchCount}
                </span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-600 dark:bg-white/10 dark:text-gray-300">
                  인원 {c.userTotal}
                </span>
                <span className="text-gray-400">›</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {/* 회사 지점 팝업 */}
      {selectedCompany && selectedCompanyData && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSelectedCompany(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-[#1F2A3B]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-start justify-between gap-2">
              <div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  {selectedCompanyData.company}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  지점 {selectedCompanyData.branchCount}개 · 인원 {selectedCompanyData.userTotal}명
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedCompany(null)}
                className="rounded-lg bg-gray-100 px-2.5 py-1 text-sm font-semibold text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200"
              >
                닫기
              </button>
            </div>

            {/* 지점 추가 */}
            <div className="mb-3 flex gap-2">
              <input
                type="text"
                value={addBranchName}
                onChange={(e) => setAddBranchName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddBranchToCompany();
                }}
                placeholder="지점명"
                className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
              />
              <button
                type="button"
                onClick={handleAddBranchToCompany}
                disabled={saving}
                className="shrink-0 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {saving ? "추가 중..." : "지점 추가"}
              </button>
            </div>

            {/* 지점 목록 */}
            <div className="max-h-[300px] space-y-1.5 overflow-y-auto pr-1">
              {selectedCompanyData.branches
                .slice()
                .sort((a, b) => a.branchName.localeCompare(b.branchName, "ko"))
                .map((b) => (
                  <div
                    key={b.branchIdx}
                    className="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-2.5 dark:border-gray-700"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {b.branchName}
                      </span>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-white/10 dark:text-gray-300">
                        소속 {b.userCount}명
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteBranch(b)}
                      className="rounded-md bg-white px-2.5 py-1 text-xs font-semibold text-red-600 shadow-sm hover:bg-red-50 dark:bg-white/10 dark:text-red-300"
                    >
                      삭제
                    </button>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* 새 회사 등록 팝업 */}
      {showAddCompany && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowAddCompany(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-[#1F2A3B]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 text-lg font-bold text-gray-900 dark:text-white">새 회사 등록</div>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              회사와 첫 지점을 함께 등록합니다. 이후 지점은 회사 팝업에서 추가하세요.
            </p>
            <div className="space-y-2">
              <input
                type="text"
                value={newCompany}
                onChange={(e) => setNewCompany(e.target.value)}
                placeholder="회사명"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
              />
              <input
                type="text"
                value={newFirstBranch}
                onChange={(e) => setNewFirstBranch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddCompany();
                }}
                placeholder="첫 지점명"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowAddCompany(false)}
                className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300 dark:bg-white/10 dark:text-gray-200"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleAddCompany}
                disabled={saving}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {saving ? "등록 중..." : "등록"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
